import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n';
import { CheckCircle, Loader2, Rocket, XCircle } from 'lucide-react';

const BoostSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const { language } = useLanguage();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus('failed');
        return;
      }

      try {
        // Poll status until paid or timeout
        let attempts = 0;
        const maxAttempts = 10;

        while (attempts < maxAttempts) {
          const response = await api.get(`/boost/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            return;
          }
          
          if (['failed', 'expired'].includes(response.data.payment_status)) {
            setStatus('failed');
            return;
          }

          // Wait 2 seconds before next check
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }

        // Timeout - assume success if we got this far
        setStatus('success');
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('failed');
      }
    };

    checkStatus();
  }, [api, sessionId]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">
              {language === 'cr' ? 'Vérifikasyon an kour...' : 'Vérification en cours...'}
            </h2>
            <p className="text-muted-foreground">
              {language === 'cr' 
                ? 'Nou i vérifié out paieman.'
                : 'Nous vérifions votre paiement.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">
              {language === 'cr' ? 'Paieman échoué' : 'Paiement échoué'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {language === 'cr' 
                ? 'Nafèr pandan lo paieman. Éséy ankor.'
                : 'Une erreur est survenue pendant le paiement. Veuillez réessayer.'}
            </p>
            <Button onClick={() => navigate('/feed')}>
              {language === 'cr' ? 'Rétourné lo fil' : 'Retour au fil'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-orange-500 h-2" />
        <CardContent className="p-8 text-center">
          <div className="relative mb-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <Rocket className="h-8 w-8 text-primary absolute -right-2 -top-2 animate-bounce" />
          </div>
          <h2 className="text-2xl font-bold mb-2">
            {language === 'cr' ? 'Boost aktivé !' : 'Boost activé !'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {language === 'cr' 
              ? 'Out post lé astèr boosté é i aparé an promyé dann fil.'
              : 'Votre post est maintenant boosté et apparaît en premier dans le fil.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/feed')}>
              {language === 'cr' ? 'Vwar lo fil' : 'Voir le fil'}
            </Button>
            <Button onClick={() => navigate('/profile')}>
              {language === 'cr' ? 'Mon profil' : 'Mon profil'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BoostSuccessPage;
